import { Head } from "@inertiajs/react"
import { ArrowUp, Search } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { csrfFetch } from "@/lib/csrf-fetch"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface Props {
  suggested_questions: string[]
}

// `trace` is only ever set on an assistant turn, and it is the reason the
// answer is trustworthy: it names the queries that produced the numbers.
interface Message {
  role: "user" | "assistant"
  content: string
  trace?: string[]
  failed?: boolean
}

interface TurnResponse {
  reply?: string
  trace?: string[]
  error?: string
}

export default function FleetChatIndex({ suggested_questions }: Props) {
  const routes = useOrgRoutes()
  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState("")
  const [pending, setPending] = useState(false)
  const scrollAnchor = useRef<HTMLDivElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Fleet Chat", href: routes.fleetChatPath() },
  ]

  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, pending])

  async function send(question: string) {
    const text = question.trim()
    if (!text || pending) return

    // The server holds no conversation: the whole history goes up each turn,
    // so this local array IS the transcript.
    const history: Message[] = [...messages, { role: "user", content: text }]
    setMessages(history)
    setDraft("")
    setPending(true)

    try {
      const response = await csrfFetch(routes.fleetChatTurnPath(), {
        method: "POST",
        body: JSON.stringify({
          messages: history.map(({ role, content }) => ({ role, content })),
        }),
      })
      const data: TurnResponse = await response.json()
      setMessages([
        ...history,
        data.error
          ? { role: "assistant", content: data.error, failed: true }
          : {
              role: "assistant",
              content: data.reply ?? "",
              trace: data.trace ?? [],
            },
      ])
    } catch {
      setMessages([
        ...history,
        {
          role: "assistant",
          content: "Could not reach the assistant. Check your connection.",
          failed: true,
        },
      ])
    } finally {
      setPending(false)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Fleet Chat" />

      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-4 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Fleet Chat</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Ask about cars, bad orders, dwell and requal. Read-only — every
            answer shows the queries it ran.
          </p>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto">
          {messages.length === 0 && (
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                Try asking
              </p>
              {suggested_questions.map((question) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => send(question)}
                  className="hover:bg-accent block w-full rounded-md border px-3 py-2 text-left text-sm"
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={cn(
                "flex",
                message.role === "user" ? "justify-end" : "justify-start",
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted",
                  message.failed && "text-destructive",
                )}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
                {message.trace && message.trace.length > 0 && (
                  <p className="text-muted-foreground mt-2 flex items-start gap-1 text-xs">
                    <Search className="mt-0.5 size-3 shrink-0" />
                    <span>checked: {message.trace.join(" · ")}</span>
                  </p>
                )}
              </div>
            </div>
          ))}

          {pending && (
            <p className="text-muted-foreground text-sm">Checking the fleet…</p>
          )}
          <div ref={scrollAnchor} />
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            send(draft)
          }}
          className="flex items-end gap-2"
        >
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, shift+Enter breaks the line — the convention
              // every operator already has in their fingers.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                send(draft)
              }
            }}
            placeholder="Ask about a car, a shop, or the whole fleet…"
            rows={2}
            className="resize-none"
          />
          <Button type="submit" size="icon" disabled={pending || !draft.trim()}>
            <ArrowUp className="size-4" />
            <span className="sr-only">Send</span>
          </Button>
        </form>
      </div>
    </AppLayout>
  )
}
